import * as React from 'react'

import {
  Box,
  Button,
  Icon,
  List,
  Spinner,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import {
  GetNotificationSettingsDocument,
  NotificationSettingsFragment,
  UpdateNotificationSettingsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

type Props = {
  settings: NotificationSettingsFragment | undefined
}

export const GeneralSettings = ({ settings }: Props) => {
  const {
    access: { id: accessId },
  } = useCurrentSession()
  const [updateNotificationSettings, { loading: updating }] = useMutation(
    UpdateNotificationSettingsDocument,
    {
      extractorFn: (data) =>
        data.notificationsMutations.updateNotificationsAccessNotificationSetting,
      refetchQueries: [
        {
          query: GetNotificationSettingsDocument,
          variables: {
            accessId: accessId,
          },
        },
      ],
    }
  )
  const [showDisableAllModal, setShowDisableAllModal] = React.useState(false)
  const [currentDisableAll, setCurrentDisableAll] = React.useState(false)

  useUnsafeEffect(() => {
    if (settings) {
      setCurrentDisableAll(settings.disableAll)
    }
  }, [settings])

  const handleOnToggleDisableAll = () => {
    if (settings?.disableAll) {
      handleDisableAll()
    } else {
      setCurrentDisableAll(!settings?.disableAll)
      setShowDisableAllModal(true)
    }
  }

  const handleDisableAll = async () => {
    const newDisableAll = !settings?.disableAll
    const unhandledResult = await updateNotificationSettings({
      variables: {
        accessId: accessId,
        disableAll: newDisableAll,
      },
    })

    handleResult(unhandledResult, () => undefined)

    setCurrentDisableAll(newDisableAll)
    setShowDisableAllModal(false)
  }

  const handleCancelDisableAll = () => {
    setCurrentDisableAll(!!settings?.disableAll)
    setShowDisableAllModal(false)
  }

  return (
    <Box gap="s24">
      <Box
        flexDirection="row"
        gap="s12"
        flexGrow
        aria-label="notification-category"
        alignItems="center"
      >
        <Icon.Large icon="send-email-envelope" />
        <Text color="grey1000" size="400" weight="medium">
          {i18n.t('notifications.settings.general_settings.title')}
        </Text>
      </Box>
      <List>
        <List.Item>
          <Box flexGrow flexDirection="column" gap="s4">
            <List.Title>
              {i18n.t('notifications.settings.general_settings.disable_all.title')}
            </List.Title>
            <List.Description>
              <Box style={{ whiteSpace: 'pre-wrap' }}>
                {i18n.t('notifications.settings.general_settings.disable_all.description')}
              </Box>
            </List.Description>
          </Box>
          {settings ? (
            <TogglerCheckbox
              disabled={updating}
              value={currentDisableAll}
              onChange={handleOnToggleDisableAll}
            />
          ) : (
            <Spinner />
          )}
        </List.Item>
      </List>
      {showDisableAllModal && (
        <ConfirmationModal
          title={i18n.t('notifications.settings.general_settings.disable_all.modal.title')}
          description={i18n.t(
            'notifications.settings.general_settings.disable_all.modal.description'
          )}
          confirmationText={i18n.t(
            'notifications.settings.general_settings.disable_all.modal.confirmation_button'
          )}
          disabled={updating}
          performAction={handleDisableAll}
          onClose={handleCancelDisableAll}
        />
      )}
    </Box>
  )
}

type ConfirmationModalProps = {
  title: string
  description: string
  confirmationText: string
  disabled: boolean
  performAction: () => void
  onClose: () => void
}

const ConfirmationModal = ({
  title,
  description,
  confirmationText,
  disabled,
  performAction,
  onClose,
}: ConfirmationModalProps) => {
  return (
    <Modal
      title={title}
      description={description}
      illustration="delete"
      onClose={onClose}
      onSubmit={performAction}
      locked={disabled}
      footer={
        <>
          <Button type="secondary" disabled={disabled} onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={disabled} submit>
            {confirmationText}
          </Button>
        </>
      }
    />
  )
}
